import { useMemo, useState } from 'react'

import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import type {
  BigStepInstance,
  WorkflowCase,
} from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'

import { Box, Text, chakra } from '@enechain/ecloud-designsystem'
import { FaCheckSolid } from '@enechain/ecloud-icons'

import { DisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { Badge } from '~/components/common/Badge'
import { iconSize } from '~/constants/iconSize'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { SubStepRow } from '~/pages/WorkflowCaseDetail/components/SubStepRow'
import {
  type BigStepDisplayStatus,
  bigStepDisplayStatusOf,
} from '~/pages/WorkflowCaseDetail/models/workflowCaseDetail'

type Props = {
  caseId: string
  bigStep: BigStepInstance
  wfCase: WorkflowCase
  isReadonly: boolean
  isLocked: boolean // 1 個前の大ステップが未完了のため操作不可
  defaultOpen?: boolean
  /**
   * 開閉が変わった時に呼ぶ。親 (`WorkflowCaseDetail`) が画面を離れて戻る間だけ
   * 覚えておくために使う (#7067)。開閉そのものは従来どおり本 component が持つ。
   */
  onOpenChange?: (isOpen: boolean) => void
  members: Member[]
  /**
   * 一覧の要承認・要対応ボタンからの focus 遷移対象サブステップの
   * display_order (spec AC-WFW-001 §243、2026-08-19 改訂 D5)。この大ステップが
   * focus 対象を含む時のみ親 (`WorkflowCaseDetail`) が渡す。該当する
   * `SubStepRow` へ `isFocusTarget` として伝搬する。
   */
  focusSubStepDisplayOrder?: number
}

const STATUS_BADGE: Record<
  BigStepDisplayStatus,
  { status: 'neutral' | 'info' | 'positive'; labelKey: string }
> = {
  notStarted: {
    status: 'neutral',
    labelKey: 'page:workflowCaseDetail.bigStep.status.notStarted',
  },
  current: {
    status: 'info',
    labelKey: 'page:workflowCaseDetail.bigStep.status.current',
  },
  complete: {
    status: 'positive',
    labelKey: 'page:workflowCaseDetail.bigStep.status.complete',
  },
}

/**
 * サブステップ一覧の左 padding。 サブステップの完了マーカー (32px 列) の中心を
 * ヘッダーの丸数字 (28px) の中心と同じ x に置く
 * (2026-10-07 デザイナー指摘「アコーディオン内のコンテンツの開始位置を、
 * ヘッダーのアイコンと縦位置を揃えたい」)。
 *
 * - ヘッダーの丸数字の中心 = padding 16 + 開閉標 32 + gap 12 + 円の半分 14 = 74px
 * - マーカーの中心 = この値 + `SubStepRow` の padding 12 + 列の半分 16
 *
 * よって 74 − 12 − 16 = 46px。 8080 (`demand/case-detail.html`) は開閉標が細いので
 * `.substeps` の 24px のまま揃っているが、 実装の開閉標は DS `AccordionIconButton`
 * と同じ 32px の器を持つため、 24px のままだと 21px 左にずれていた。
 * 開閉標・丸数字・`SubStepRow` の padding を変えたらこの値も合わせること。
 */
const SUBSTEPS_PADDING_LEFT = '46px'

/**
 * 大ステップアコーディオン行 (design/demand/case-detail.html `.bigstep` / `.bigstep-header`
 * 相当)。 丸数字 + タイトル + ステータスバッジを表示する (ステップ別の進捗ゲージは 2026-10-07 に撤去)。
 *
 * 大ステップ単位の担当者表示は設けない (spec AC-005 注釈: 業務進行はサブステップ単位、
 * 2026-07-16 PdM 判断 #3076 問2-4)。担当者はサブステップ単位で SubStepRow が表示する。
 */
export const BigStepAccordion = ({
  caseId,
  bigStep,
  wfCase,
  isReadonly,
  isLocked,
  defaultOpen = false,
  onOpenChange,
  members,
  focusSubStepDisplayOrder,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const toggleOpen = (): void => {
    setIsOpen((prev) => {
      const next = !prev
      onOpenChange?.(next)
      return next
    })
  }

  const sortedSubSteps = useMemo(
    () =>
      bigStep.subSteps.slice().sort((a, b) => a.displayOrder - b.displayOrder),
    [bigStep.subSteps],
  )

  const doneCount = sortedSubSteps.filter((ss) => ss.isCompleted).length
  const totalCount = sortedSubSteps.length
  const displayStatus = bigStepDisplayStatusOf(doneCount, totalCount, isLocked)
  const badge = STATUS_BADGE[displayStatus]

  return (
    <Box
      overflow="hidden"
      marginBottom="8px"
      background="Surface.primary"
      opacity={isLocked ? 0.55 : 1}
      borderWidth="1px"
      borderColor={
        displayStatus === 'complete'
          ? 'Border.positiveMid'
          : displayStatus === 'current'
            ? 'Border.accentPrimary'
            : 'Border.lowEmphasis'
      }
      borderRadius="base"
    >
      {/* アコーディオンヘッダー */}
      <chakra.button
        alignItems="center"
        gap="12px"
        display="flex"
        width="100%"
        padding="12px 16px"
        textAlign="left"
        background="none"
        border="none"
        // design .bigstep-header:hover = --hover-low-emphasis。 Surface.secondary は
        // 隣接する完了行の地と同色で、 hover と状態表示が見分けづらかった。
        _hover={isLocked ? undefined : { background: 'Hover.lowEmphasis' }}
        cursor={isLocked ? 'not-allowed' : 'pointer'}
        disabled={isLocked}
        onClick={() => {
          if (!isLocked) toggleOpen()
        }}
        type="button"
      >
        {/* 開閉標は header の**左端**で、 DS `AccordionIconButton` と同じ見た目に
            揃える (2026-08-20 デザイナー指摘)。 回転ではなくグリフ差し替え
            (閉 = chevron-right / 開 = chevron-down) にするのも DS / 8080 に合わせるため。 */}
        <DisclosureIcon expanded={isOpen} />

        <Box
          alignItems="center"
          flexWrap="wrap"
          flex={1}
          gap="12px"
          display="flex"
        >
          {/* ステップ番号バッジ */}
          {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design .step-circle
              (components.css:1503) = sm/600。数値 circle はバッジ系トラックで是正予定 */}
          <Box
            as="span"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            display="inline-flex"
            width="28px"
            height="28px"
            color={
              displayStatus === 'complete'
                ? 'Text.highEmphasisInverse'
                : 'Text.midEmphasis'
            }
            fontSize="md"
            fontWeight="semibold"
            // design `.step-circle` (components.css:1977-2005) 準拠:
            //   未到達 = border midEmphasis / 進行中 (.active) = border accentPrimary /
            //   完了 (.done) = 地・枠とも surface-accent-primary + 反転文字。
            // 完了を positive (緑) にしていたが、 DS StepIndicator は完了も accent で、
            // 緑は「良い終端」 を表す別の意味を持つ (2026-08-17 デザイナー指摘)。
            background={
              displayStatus === 'complete'
                ? 'Surface.accentPrimary'
                : 'Surface.primary'
            }
            borderWidth="2px"
            borderColor={
              displayStatus === 'notStarted'
                ? 'Border.midEmphasis'
                : displayStatus === 'complete'
                  ? 'Surface.accentPrimary'
                  : 'Border.accentPrimary'
            }
            borderRadius="50%"
          >
            {/* 完了は番号ではなくチェックに置き換える。 design `.step-circle.done`
                (components.css:2000-2018) が `font-size: 0` + ::before のチェック
                マスクで番号を隠す作りで、 同じ大ステップを描く WizardStepper も
                完了で FaCheckSolid に切り替えている。 ここだけ番号 + 緑バッジで
                完了を表しており、 同一画面内で完了の表し方が 2 通りあった
                (2026-08-17 デザイナー指摘「2 つの UI のステータス表現は一致すべき」)。
                グリフは DS の mask-size 60% (28px 円 → 約 17px) に合わせて 16px。 */}
            {displayStatus === 'complete' ? (
              <FaCheckSolid boxSize={iconSize.stepperMark} />
            ) : (
              bigStep.displayOrder
            )}
          </Box>

          {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design .bigstep-title
              (case-detail.html:39) = lg/700 と一致。lg/bold は 1 箇所のみの組のため
              role 追加せず例外化 */}
          <Text color="Text.highEmphasis" fontSize="lg" fontWeight="bold">
            {localizeBuiltinWorkflowText(bigStep.title, t)}
          </Text>

          <Badge status={badge.status}>{t(badge.labelKey)}</Badge>
        </Box>

        {/* 見出し右端の進捗ゲージ (バー + 「1 / 1」) は撤去 (2026-10-07 デザイナー指摘「このゲージ
            不要かも。 業務上問題なさそうなので削除」)。 ケース全体の進捗は CaseHeader に残す
            (spec §機能詳細「進捗バー」)。 doneCount / totalCount は状態判定に引き続き使う。 */}
      </chakra.button>

      {/* サブステップリスト */}
      {isOpen && (
        <Box
          // 左は SUBSTEPS_PADDING_LEFT (サブステップの完了マーカーをヘッダーの丸数字の
          // 真下に置く)。 右 24px は design `.substeps` = `padding: var(--space-2) 24px`
          // 準拠で、 内側の `SubStepRow` が持つ 12px と合わせて 36px の inset
          // (デザイナー指摘 2026-08-28「右側の余白を左と合わせたい」)。
          // 縦 (4px / 8px) は既存のまま。
          paddingTop="4px"
          paddingRight="24px"
          paddingBottom="8px"
          paddingLeft={SUBSTEPS_PADDING_LEFT}
          background="Surface.secondary"
          borderColor="Border.lowEmphasis"
          borderTopWidth="1px"
        >
          {sortedSubSteps.map((ss, ssIndex) => (
            <SubStepRow
              key={`${bigStep.displayOrder}-${ss.displayOrder}`}
              bigStepDisplayOrder={bigStep.displayOrder}
              caseId={caseId}
              displayNumber={ssIndex + 1}
              isFocusTarget={ss.displayOrder === focusSubStepDisplayOrder}
              isLast={ssIndex === sortedSubSteps.length - 1}
              isReadonly={isReadonly}
              members={members}
              subStep={ss}
              wfCase={wfCase}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
